import { useCallback } from 'react';
import { useToast } from '../../app/useToast';
import { useAuthStore } from '../../context/AuthContext';
import useApi from '../../api/useApi';
import { AlertCheckRequest, AlertData, AlertType } from '../../types/alerts/AlertTypes';
import { useCheckInhibitorQuestionnaire } from './checks/inhibitorQuestionnaireCheck';
import { buildQueryParams } from '../../api/endpoints/queryParamsHelper';

/**
 * Simple alert engine hook for sending alerts to the API
 */
export const useAlertEngine = () => {
    const { toastError } = useToast();
    const { loggedInUser, signalRConnectionId } = useAuthStore();
    const { postApiData } = useApi();
    const { checkInhibitorQuestionnaire } = useCheckInhibitorQuestionnaire();

    /**
     * Sends alert data to the API
     */
    const sendAlert = useCallback(
        async (alertData: AlertData[]): Promise<boolean> => {
            if (!loggedInUser) {
                console.error('Cannot send alert: User not authenticated');
                return false;
            }

            if (!alertData || alertData.length === 0) {
                console.warn('No alert data provided');
                return false;
            }

            try {
                const payload = alertData.map((data) => ({
                    alertTypeId: data.alertTypeId,
                    vesselName: data.vesselName,
                    imoNumber: data.imoNumber,
                    value: data.value,
                    limit: data.limit,
                    highSeverity: data.highSeverity,
                    cargoNumber: data.cargoNumber,
                    alertSourceDate: data.alertSourceDate?.toISOString(),
                }));

                const result = await postApiData({
                    queryParam: buildQueryParams({
                        imoNumber: alertData[0].imoNumber,
                        connectionId: signalRConnectionId,
                    }),
                    endpoint: 'AddAlerts',
                    data: payload,
                });

                if (result) {
                    console.log('Alert sent successfully');
                    return true;
                }
                return false;
            } catch (error) {
                console.error('Failed to send alert:', error);
                toastError('Failed to send alert to server');
                return false;
            }
        },
        [loggedInUser, postApiData, toastError]
    );

    /**
     * Check for alerts based on alert type and context data
     */
    const checkAlert = useCallback(
        async (alertCheckRequest: AlertCheckRequest): Promise<void> => {
            const alerts: AlertData[] | undefined = [];
            switch (alertCheckRequest.alertType) {
                case AlertType.MISSING_INHIBITOR_QUESTIONNAIRE: {
                    alerts.push(...(await checkInhibitorQuestionnaire(alertCheckRequest.contextData)));
                    break;
                }
                default:
                    console.warn(`Unknown alert type: ${alertCheckRequest.alertType}`);
                    return;
            }

            if (alerts) {
                console.log(alerts);
                await sendAlert(alerts);
            }
        },
        [sendAlert]
    );

    return {
        sendAlert,
        checkAlert,
    };
};

export default useAlertEngine;
